import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import ChevronDownIcon from '../../components/Icons/ChevronDownIcon';
import Button from '../../components/Button/Button';
import LocationIcon from '../../components/Icons/LocationIcon';
import InputText from '../../components/Input/InputText';

import Typography from '../../components/Typography';
import CardToogle from '../../components/Toogle/CardToogle';
import { getEventTypeSvgIcon, getEventTerrainSvgIcon } from '../../utilities';
import { theme } from '../../theme';
import { EVENT_TYPES, TERRAIN_TYPES, EXPERIENCE_LEVELS } from '../../constants/eventTypes';

const InterestsStep = ({ interests, setInterests, onUseDeviceLocation, isLocating }) => {
  const { t } = useTranslation('completeProfile');
  const [collapsed, setCollapsed] = React.useState({
    eventTypes: false,
    terrains: false,
    vehicleTypes: false,
    experiences: false,
    attendance: false,
    organizing: false,
    professional: false,
    suggestions: false,
  });

  const toggleSection = (key) => {
    setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleToggleEventType = (eventType) => {
    setInterests(prev => ({
      ...prev,
      eventTypes: prev.eventTypes.includes(eventType)
        ? prev.eventTypes.filter(type => type !== eventType)
        : [...prev.eventTypes, eventType]
    }));
  };

  const handleToggleTerrain = (terrain) => {
    setInterests(prev => ({
      ...prev,
      terrains: prev.terrains.includes(terrain)
        ? prev.terrains.filter(t => t !== terrain)
        : [...prev.terrains, terrain]
    }));
  };

  const handleToggleExperience = (experience) => {
    setInterests(prev => ({
      ...prev,
      experiences: prev.experiences.includes(experience)
        ? prev.experiences.filter(exp => exp !== experience)
        : [...prev.experiences, experience]
    }));
  };

  const handleToggleVehicleType = (vehicleType) => {
    setInterests(prev => ({
      ...prev,
      vehicleTypes: prev.vehicleTypes.includes(vehicleType)
        ? prev.vehicleTypes.filter(type => type !== vehicleType)
        : [...prev.vehicleTypes, vehicleType]
    }));
  };

  const handleToggleFrequency = (frequency) => {
    setInterests(prev => ({
      ...prev,
      eventFrequency: frequency
    }));
  };

  const handleToggleOrganizingFrequency = (frequency) => {
    setInterests(prev => ({
      ...prev,
      organizingFrequency: frequency
    }));
  };

  const handleToggleProfessionalOrganizer = (value) => {
    setInterests(prev => ({
      ...prev,
      professionalOrganizer: value
    }));
  };

  const handleToggleEventSuggestions = (value) => {
    setInterests(prev => ({
      ...prev,
      eventSuggestions: value
    }));
  };

  // Datos de los intereses con traducciones
  const eventTypes = EVENT_TYPES.map(type => ({
    id: type,
    name: t(`interests.eventTypes.${type}`)
  }));

  const terrains = TERRAIN_TYPES.map(terrain => ({
    id: terrain,
    name: t(`interests.terrains.${terrain}`)
  }));

  const experiences = EXPERIENCE_LEVELS.map(level => ({
    id: level,
    name: t(`interests.experiences.${level}`)
  }));

  const vehicleTypes = [
    { id: 'sport-bike', name: t('interests.vehicleTypes.sport-bike') },
    { id: 'naked-bike', name: t('interests.vehicleTypes.naked-bike') },
    { id: 'scooter', name: t('interests.vehicleTypes.scooter') },
    { id: 'custom-bike', name: t('interests.vehicleTypes.custom-bike') },
    { id: 'dual-sport-bike', name: t('interests.vehicleTypes.dual-sport-bike') },
    { id: 'enduro-bike', name: t('interests.vehicleTypes.enduro-bike') },
    { id: 'trial-bike', name: t('interests.vehicleTypes.trial-bike') },
    { id: 'touring-bike', name: t('interests.vehicleTypes.touring-bike') },
    { id: 'electric-bike', name: t('interests.vehicleTypes.electric-bike') },
    { id: 'mini-bikes', name: t('interests.vehicleTypes.mini-bikes') },
    { id: 'pit-bikes', name: t('interests.vehicleTypes.pit-bikes') },
    { id: 'motocross-bike', name: t('interests.vehicleTypes.motocross-bike') },
    { id: 'motorized-tricycle', name: t('interests.vehicleTypes.motorized-tricycle') },
    { id: 'trike', name: t('interests.vehicleTypes.trike') },
    { id: 'sidecar-bike', name: t('interests.vehicleTypes.sidecar-bike') },
    { id: 'compact-car', name: t('interests.vehicleTypes.compact-car') },
    { id: 'sedan', name: t('interests.vehicleTypes.sedan') },
    { id: 'coupe', name: t('interests.vehicleTypes.coupe') },
    { id: 'convertible', name: t('interests.vehicleTypes.convertible') },
    { id: 'suv', name: t('interests.vehicleTypes.suv') },
    { id: 'crossover', name: t('interests.vehicleTypes.crossover') },
    { id: 'pickup-truck', name: t('interests.vehicleTypes.pickup-truck') },
    { id: 'off-road-vehicle', name: t('interests.vehicleTypes.off-road-vehicle') },
    { id: 'buggy', name: t('interests.vehicleTypes.buggy') },
    { id: 'atv', name: t('interests.vehicleTypes.atv') },
    { id: '4x4', name: t('interests.vehicleTypes.4x4') },
    { id: 'go-kart', name: t('interests.vehicleTypes.go-kart') }
  ];

  const frequencies = [
    { id: 'weekly', name: t('interests.attendanceFrequencies.weekly') },
    { id: 'monthly', name: t('interests.attendanceFrequencies.monthly') },
    { id: 'special-getaways', name: t('interests.attendanceFrequencies.special-getaways') },
    { id: 'when-interesting', name: t('interests.attendanceFrequencies.when-interesting') }
  ];

  const organizingFrequencies = [
    { id: 'weekly', name: t('interests.organizingFrequencies.weekly') },
    { id: 'monthly', name: t('interests.organizingFrequencies.monthly') },
    { id: 'special-getaways', name: t('interests.organizingFrequencies.special-getaways') },
    { id: 'when-interesting', name: t('interests.organizingFrequencies.when-interesting') }
  ];

  const professionalOrganizerOptions = [
    { id: 'yes', name: t('interests.professionalOrganizer.yes') },
    { id: 'no', name: t('interests.professionalOrganizer.no') },
    { id: 'not-sure', name: t('interests.professionalOrganizer.notSure') }
  ];

  const eventSuggestionsOptions = [
    { id: true, name: t('interests.eventSuggestions.yes') },
    { id: false, name: t('interests.eventSuggestions.no') }
  ];

  return (
    <InterestsContainer>
      {/* Introducción */}
      <IntroSection>
        <Typography as="h3" $variant="title-5-semibold" $variantTablet="body-1-semibold" $variantMobile="body-1-semibold" color={theme.colors.defaultStrong}>
          {t('interests.title')}
        </Typography>
        <Typography as="p" $variant="body-1-medium" $variantTablet="body-2-medium" $variantMobile="body-2-medium" color={theme.colors.defaultWeak}>
          {t('interests.subtitle')}
        </Typography>
      </IntroSection>

      {/* ¿Qué tipo de plan te emociona más? */}
      <InterestSection>
        <CollapsibleHeader onClick={() => toggleSection('eventTypes')}>
          <SectionTitle>
            <Typography as="h3" $variant="title-5-semibold" $variantTablet="body-1-semibold" $variantMobile="body-2-semibold" color={theme.colors.defaultStrong}>
              {t('interests.eventTypes.title')}
            </Typography>
          </SectionTitle>
          <ChevronIcon $open={!collapsed.eventTypes} fill={theme.colors.defaultStrong} width={16} height={16} />
        </CollapsibleHeader>
        {!collapsed.eventTypes && (
          <SuggestionHint $variant="body-2-regular" color={theme.colors.defaultWeak}>
            {t('interests.eventTypes.subtitle')}
          </SuggestionHint>
        )}
        {!collapsed.eventTypes && (
          <CardsGrid>
            {eventTypes.map((eventType) => (
              <CardToogle
                key={eventType.id}
                isActive={interests.eventTypes.includes(eventType.id)}
                onClick={() => handleToggleEventType(eventType.id)}
                // icon={getEventTypeSvgIcon(eventType.id, interests.eventTypes.includes(eventType.id) ? theme.colors.brandMain : theme.colors.defaultSubtle)}
                category={eventType.name}
                textAlign="center"
              />
            ))}
          </CardsGrid>
        )}
      </InterestSection>

      {/* ¿En qué terrenos te gusta rodar? */}
      <InterestSection>
        <CollapsibleHeader onClick={() => toggleSection('terrains')}>
          <SectionTitle>
            <Typography as="h3" $variant="title-5-semibold" $variantTablet="body-1-semibold" $variantMobile="body-2-semibold" color={theme.colors.defaultStrong}>
              {t('interests.terrains.title')}
            </Typography>
          </SectionTitle>
          <ChevronIcon $open={!collapsed.terrains} fill={theme.colors.defaultStrong} width={16} height={16} />
        </CollapsibleHeader>
        {!collapsed.terrains && (
          <SuggestionHint $variant="body-2-regular" color={theme.colors.defaultWeak}>
            {t('interests.terrains.subtitle')}
          </SuggestionHint>
        )}
        {!collapsed.terrains && (
          <CardsGrid>
            {terrains.map((terrain) => (
              <CardToogle
                key={terrain.id}
                isActive={interests.terrains.includes(terrain.id)}
                onClick={() => handleToggleTerrain(terrain.id)}
                // icon={getEventTerrainSvgIcon(terrain.id, interests.terrains.includes(terrain.id) ? theme.colors.brandMain : theme.colors.defaultSubtle)}
                category={terrain.name}
                textAlign="center"
              />
            ))}
          </CardsGrid>
        )}
      </InterestSection>

      {/* ¿Qué tipos de vehículos te interesan? */}
      <InterestSection>
        <CollapsibleHeader onClick={() => toggleSection('vehicleTypes')}>
          <SectionTitle>
            <Typography as="h3" $variant="title-5-semibold" $variantTablet="body-1-semibold" $variantMobile="body-2-semibold" color={theme.colors.defaultStrong}>
              {t('interests.vehicleTypes.title')}
            </Typography>
          </SectionTitle>
          <ChevronIcon $open={!collapsed.vehicleTypes} fill={theme.colors.defaultStrong} width={16} height={16} />
        </CollapsibleHeader>
        {!collapsed.vehicleTypes && (
          <SuggestionHint $variant="body-2-regular" color={theme.colors.defaultWeak}>
            {t('interests.vehicleTypes.subtitle')}
          </SuggestionHint>
        )}
        {!collapsed.vehicleTypes && (
          <CardsGrid>
            {vehicleTypes.map((vehicleType) => (
              <CardToogle
                key={vehicleType.id}
                isActive={interests.vehicleTypes.includes(vehicleType.id)}
                onClick={() => handleToggleVehicleType(vehicleType.id)}
                category={vehicleType.name}
                textAlign="center"
              />
            ))}
          </CardsGrid>
        )}
      </InterestSection>

      {/* ¿Qué nivel de experiencia tienes? */}
      <InterestSection>
        <CollapsibleHeader onClick={() => toggleSection('experiences')}>
          <SectionTitle>
            <Typography as="h3" $variant="title-5-semibold" $variantTablet="body-1-semibold" $variantMobile="body-2-semibold" color={theme.colors.defaultStrong}>
              {t('interests.experiences.title')}
            </Typography>
          </SectionTitle>
          <ChevronIcon $open={!collapsed.experiences} fill={theme.colors.defaultStrong} width={16} height={16} />
        </CollapsibleHeader>
        {!collapsed.experiences && (
          <SuggestionHint $variant="body-2-regular" color={theme.colors.defaultWeak}>
            {t('interests.experiences.subtitle')}
          </SuggestionHint>
        )}
        {!collapsed.experiences && (
          <CardsTrio>
            {experiences.map((experience) => (
              <CardToogle
                key={experience.id}
                isActive={interests.experiences.includes(experience.id)}
                onClick={() => handleToggleExperience(experience.id)}
                category={experience.name}
                textAlign="center"
              />
            ))}
          </CardsTrio>
        )}
      </InterestSection>

      {/* ¿Con qué frecuencia te gustaría participar? */}
      <InterestSection>
        <CollapsibleHeader onClick={() => toggleSection('attendance')}>
          <SectionTitle>
            <Typography as="h3" $variant="title-5-semibold" $variantTablet="body-1-semibold" $variantMobile="body-2-semibold" color={theme.colors.defaultStrong}>
              {t('interests.attendanceFrequencies.title')}
            </Typography>
          </SectionTitle>
          <ChevronIcon $open={!collapsed.attendance} fill={theme.colors.defaultStrong} width={16} height={16} />
        </CollapsibleHeader>
        {!collapsed.attendance && (
          <SuggestionHint $variant="body-2-regular" color={theme.colors.defaultWeak}>
            {t('interests.attendanceFrequencies.subtitle')}
          </SuggestionHint>
        )}
        {!collapsed.attendance && (
          <FrequencyGrid>
            {frequencies.map((frequency) => (
              <CardToogle
                key={frequency.id}
                isActive={interests.eventFrequency === frequency.id}
                onClick={() => handleToggleFrequency(frequency.id)}
                category={frequency.name}
                textAlign="center"
              />
            ))}
          </FrequencyGrid>
        )}
      </InterestSection>

      {/* ¿Con qué frecuencia te gustaría organizar eventos o actividades? */}
      <InterestSection>
        <CollapsibleHeader onClick={() => toggleSection('organizing')}>
          <SectionTitle>
            <Typography as="h3" $variant="title-5-semibold" $variantTablet="body-1-semibold" $variantMobile="body-2-semibold" color={theme.colors.defaultStrong}>
              {t('interests.organizingFrequencies.title')}
            </Typography>
          </SectionTitle>
          <ChevronIcon $open={!collapsed.organizing} fill={theme.colors.defaultStrong} width={16} height={16} />
        </CollapsibleHeader>
        {!collapsed.organizing && (
          <SuggestionHint $variant="body-2-regular" color={theme.colors.defaultWeak}>
            {t('interests.organizingFrequencies.subtitle')}
          </SuggestionHint>
        )}
        {!collapsed.organizing && (
          <FrequencyGrid>
            {organizingFrequencies.map((frequency) => (
              <CardToogle
                key={frequency.id}
                isActive={interests.organizingFrequency === frequency.id}
                onClick={() => handleToggleOrganizingFrequency(frequency.id)}
                category={frequency.name}
                textAlign="center"
              />
            ))}
          </FrequencyGrid>
        )}
      </InterestSection>

      {/* ¿Te interesa organizar eventos de forma profesional? */}
      <InterestSection>
        <CollapsibleHeader onClick={() => toggleSection('professional')}>
          <SectionTitle>
            <Typography as="h3" $variant="title-5-semibold" $variantTablet="body-1-semibold" $variantMobile="body-2-semibold" color={theme.colors.defaultStrong}>
              {t('interests.professionalOrganizer.title')}
            </Typography>
          </SectionTitle>
          <ChevronIcon $open={!collapsed.professional} fill={theme.colors.defaultStrong} width={16} height={16} />
        </CollapsibleHeader>
        {!collapsed.professional && (
          <CardsTrio style={{ 'marginTop': '8px' }}>
            {professionalOrganizerOptions.map((option) => (
              <CardToogle
                key={option.id}
                isActive={interests.professionalOrganizer === option.id}
                onClick={() => handleToggleProfessionalOrganizer(option.id)}
                category={option.name}
                textAlign="center"
              />
            ))}
          </CardsTrio>
        )}
      </InterestSection>

      {/* ¿Te gustaría recibir sugerencias de eventos? */}
      <InterestSection>
        <CollapsibleHeader onClick={() => toggleSection('suggestions')}>
          <SectionTitle>
            <Typography as="h3" $variant="title-5-semibold" $variantTablet="body-1-semibold" $variantMobile="body-2-semibold" color={theme.colors.defaultStrong}>
              {t('interests.eventSuggestions.title')}
            </Typography>
          </SectionTitle>
          <ChevronIcon $open={!collapsed.suggestions} fill={theme.colors.defaultStrong} width={16} height={16} />
        </CollapsibleHeader>
        {!collapsed.suggestions && (
          <SuggestionHint $variant="body-2-regular" color={theme.colors.defaultWeak}>
            {t('interests.eventSuggestions.subtitle')}
          </SuggestionHint>
        )}
        {!collapsed.suggestions && (
          <CardsGrid>
            {eventSuggestionsOptions.map((option) => (
              <CardToogle
                key={option.id.toString()}
                isActive={interests.eventSuggestions === option.id}
                onClick={() => handleToggleEventSuggestions(option.id)}
                category={option.name}
                textAlign="center"
              />
            ))}
          </CardsGrid>
        )}
        {!collapsed.suggestions && interests.eventSuggestions && (
          <NearbyCityWrapper>
            <Typography as="label" $variant="body-2-medium" color={theme.colors.defaultStrong}>
              {t('interests.nearbyCity.label')}
            </Typography>
            <InputText
              type="text"
              name="nearbyCity"
              value={interests.nearbyCity || ''}
              onChange={(e) => setInterests(prev => ({ ...prev, nearbyCity: e.target.value }))}
              $size=""
              placeholder={t('interests.nearbyCity.placeholder')}
            />
            <Button $variant="ghost" size="m" onClick={onUseDeviceLocation} disabled={isLocating} aria-label="use-device-location">
              <LocationIcon width={16} height={16} fill={theme.colors.defaultMain} />
              {t('settings.notifications.labels.useDeviceLocation', { defaultValue: 'Obtener mi ubicación' })}
            </Button>
          </NearbyCityWrapper>
        )}
      </InterestSection>
    </InterestsContainer>
  );
};

InterestsStep.propTypes = {
  interests: PropTypes.shape({
    eventTypes: PropTypes.arrayOf(PropTypes.string),
    terrains: PropTypes.arrayOf(PropTypes.string),
    experiences: PropTypes.arrayOf(PropTypes.string),
    vehicleTypes: PropTypes.arrayOf(PropTypes.string),
    eventFrequency: PropTypes.string,
    organizingFrequency: PropTypes.string,
    professionalOrganizer: PropTypes.string,
    eventSuggestions: PropTypes.bool
  }).isRequired,
  setInterests: PropTypes.func.isRequired,
  onUseDeviceLocation: PropTypes.func,
  isLocating: PropTypes.bool
};

export default InterestsStep;

// Styled Components
const InterestsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.md};
  width: 100%;
`;

const IntroSection = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.border.defaultWeak};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xs};
  padding-bottom: ${({ theme }) => theme.sizing.md};
  text-align: center;
`;

const InterestSection = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.border.defaultWeak};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xs};
  padding-bottom: ${({ theme }) => theme.sizing.md};

  &:last-child {
    border-bottom: none;
  }
`;

const SectionTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xxs};
`;

const CardsGrid = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.sizing.xs};
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  width: 100%;
`;

const FrequencyGrid = styled(CardsGrid)`
  grid-template-columns: repeat(2, 1fr);
`;

const CardsTrio = styled(CardsGrid)`
  // grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const NearbyCityWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xs};
  margin-top: ${({ theme }) => theme.sizing.xs};
`;

const CollapsibleHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  cursor: pointer;
`;

const ChevronIcon = styled(ChevronDownIcon)`
  transition: transform 0.2s ease;
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'rotate(0deg)')};
  flex-shrink: 0;
  margin-top: 2px;
`;

const SuggestionHint = styled(Typography)`
  padding-bottom: ${({ theme }) => theme.sizing.xs};
`;
